import { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Zap, Clock, Trophy, Settings, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AlertOptIn — Shown after user follows a team or sets a reminder.
 * Non-aggressive, value-first prompt.
 */
export function AlertOptIn({ onClose, teamName }) {
  const [step, setStep] = useState('prompt'); // prompt | requesting | done | denied
  const token = localStorage.getItem('token');

  const requestPermission = async () => {
    setStep('requesting');
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const reg = await navigator.serviceWorker.ready;
        const vapidRes = await fetch(`${API}/api/push/vapid-public-key`);
        const { publicKey } = await vapidRes.json();
        if (!publicKey) { setStep('done'); onClose(); return; }

        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
        const subJson = sub.toJSON();

        await fetch(`${API}/api/alerts/subscribe-push`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ endpoint: subJson.endpoint, keys: subJson.keys }),
        });

        setStep('done');
        setTimeout(onClose, 1500);
      } else {
        setStep('denied');
        setTimeout(onClose, 2000);
      }
    } catch (e) {
      console.error('Push subscribe error:', e);
      setStep('denied');
      setTimeout(onClose, 2000);
    }
  };

  if (!('Notification' in window) || !('serviceWorker' in navigator)) return null;
  if (Notification.permission === 'granted') return null; // already enabled

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-in slide-in-from-bottom duration-300" data-testid="alert-opt-in">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-4 shadow-2xl shadow-black/50">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/30 hover:text-white/60 transition-colors" data-testid="alert-opt-in-close">
          <X className="w-4 h-4" />
        </button>

        {step === 'prompt' && (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                <Bell className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">Never miss a goal</h4>
                <p className="text-white/50 text-xs">Get alerts when {teamName || 'your teams'} score</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mb-3 text-white/40 text-[11px]">
              <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-emerald-400" /> Goals</span>
              <span className="flex items-center gap-1"><Trophy className="w-3 h-3 text-emerald-400" /> Full Time</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-white/30" /> Kickoff (opt.)</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={requestPermission}
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors"
                data-testid="alert-enable-btn"
              >
                Enable Alerts
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/50 text-xs font-medium rounded-lg transition-colors"
                data-testid="alert-later-btn"
              >
                Later
              </button>
            </div>
          </>
        )}

        {step === 'requesting' && (
          <div className="flex items-center gap-3 py-2">
            <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-white/70 text-sm">Setting up alerts...</p>
          </div>
        )}

        {step === 'done' && (
          <div className="flex items-center gap-3 py-2">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Bell className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-emerald-400 text-sm font-semibold">Alerts enabled!</p>
              <p className="text-white/50 text-xs">You'll get push notifications for goals and results.</p>
            </div>
          </div>
        )}

        {step === 'denied' && (
          <div className="flex items-center gap-3 py-2">
            <BellOff className="w-5 h-5 text-white/30" />
            <p className="text-white/50 text-sm">No worries — you can enable alerts anytime in settings.</p>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * AlertSettings — Notification preferences panel for Dashboard/settings.
 */
export function AlertSettings() {
  const [prefs, setPrefs] = useState(null);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  const fetchPrefs = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/alerts/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPrefs(data.preferences);
      setPushSubscribed(data.push_subscribed);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const updatePref = async (key, value) => {
    const newPrefs = { ...prefs, [key]: value };
    setPrefs(newPrefs);
    setSaving(true);
    try {
      await fetch(`${API}/api/alerts/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled: newPrefs.enabled,
          goals: newPrefs.goals,
          kickoff: newPrefs.kickoff,
          full_time: newPrefs.full_time,
        }),
      });
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  const enablePush = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const reg = await navigator.serviceWorker.ready;
      const vapidRes = await fetch(`${API}/api/push/vapid-public-key`);
      const { publicKey } = await vapidRes.json();
      if (!publicKey) return;

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const subJson = sub.toJSON();

      await fetch(`${API}/api/alerts/subscribe-push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ endpoint: subJson.endpoint, keys: subJson.keys }),
      });
      setPushSubscribed(true);
      updatePref('enabled', true);
    } catch (e) {
      console.error('Push enable error:', e);
    }
  };

  const disablePush = async () => {
    try {
      await fetch(`${API}/api/alerts/unsubscribe-push`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setPushSubscribed(false);
      updatePref('enabled', false);
    } catch { /* silent */ }
  };

  if (!prefs) return null;

  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden" data-testid="alert-settings">
      <div className="p-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-emerald-500" />
          <h3 className="text-white font-bold text-sm">Match Alerts</h3>
        </div>
        {saving && <span className="text-emerald-400 text-[10px]">Saving...</span>}
      </div>

      <div className="p-4 space-y-4">
        {/* Push toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-medium">Push Notifications</p>
            <p className="text-white/40 text-xs">Receive browser alerts for followed teams</p>
          </div>
          {pushSubscribed ? (
            <button onClick={disablePush} className="px-3 py-1.5 bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-semibold rounded-lg" data-testid="disable-push-btn">
              Disable
            </button>
          ) : (
            <button onClick={enablePush} className="px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-semibold rounded-lg" data-testid="enable-push-btn">
              Enable
            </button>
          )}
        </div>

        {/* Alert type toggles */}
        {prefs.enabled && (
          <div className="space-y-3 pt-2 border-t border-white/5">
            <p className="text-white/40 text-[10px] uppercase tracking-wider font-bold">Alert Types</p>
            <ToggleRow
              icon={<Zap className="w-4 h-4 text-emerald-400" />}
              label="Goals"
              description="Immediate alert when a followed team scores"
              checked={prefs.goals}
              onChange={(v) => updatePref('goals', v)}
              testId="toggle-goals"
            />
            <ToggleRow
              icon={<Clock className="w-4 h-4 text-sky-400" />}
              label="Match Start"
              description="Alert when a followed team's match kicks off"
              checked={prefs.kickoff}
              onChange={(v) => updatePref('kickoff', v)}
              testId="toggle-kickoff"
            />
            <ToggleRow
              icon={<Trophy className="w-4 h-4 text-amber-400" />}
              label="Full Time"
              description="Final score summary when the match ends"
              checked={prefs.full_time}
              onChange={(v) => updatePref('full_time', v)}
              testId="toggle-fulltime"
            />
          </div>
        )}
      </div>
    </div>
  );
}


function ToggleRow({ icon, label, description, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <div className="flex items-center gap-2.5">
        {icon}
        <div>
          <p className="text-white text-sm">{label}</p>
          <p className="text-white/30 text-[11px]">{description}</p>
        </div>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`w-10 h-5 rounded-full transition-colors relative ${checked ? 'bg-emerald-500' : 'bg-white/10'}`}
        data-testid={`${testId}-toggle`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'left-[22px]' : 'left-0.5'}`} />
      </button>
    </div>
  );
}


function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
